import { ColumnOption as Metric } from "@/types";
import { Reorder } from "framer-motion";
import { DragHandleDots2Icon } from "@radix-ui/react-icons";
import { RiCloseLine } from "@remixicon/react";
import { getPlatformIcon } from "@/screens/utils/Helpers";
import { IconDatabase } from "@tabler/icons-react";
import {
  SqTooltip,
  SqTooltipContent,
  SqTooltipProvider,
  SqTooltipTrigger,
} from "@/components/squircle-ui/tooltip";
import { SqButton } from "@/components/squircle-ui/button";
import { useState, useEffect, useRef } from "react";

// Animation delay to allow drop animation to complete
const DROP_ANIMATION_DELAY = 300;

interface Props {
  selectedMetrics: Metric[];
  onMetricChange: (metrics: Metric[]) => void;
}

const SelectedMetricItem = ({
  metric,
  onRemove,
  isBeingDragged = false,
}: {
  metric: Metric;
  onRemove: (metric: Metric) => void;
  isBeingDragged?: boolean;
}) => {
  return (
    <SqTooltip key={metric.key}>
      <SqTooltipTrigger asChild>
        <SqButton
          variant={"default"}
          bgColor="white"
          hoverBgColor="white"
          radius={6}
          size={"sm"}
          shadow={"sm"}
          hasOutline
          className={`group/item transition-all duration-150 gap-0 px-2 py-1 ${isBeingDragged ? "cursor-grabbing" : "cursor-grab active:cursor-grabbing"}`}
        >
          <DragHandleDots2Icon
            aria-label="Reorder metric"
            fontSize={12}
            className="text-brand-primary-400 mr-1 overflow-hidden shrink-0"
          />
          {/* showing platform icons for selected metrics */}
          {metric?.show_platforms &&
            metric?.platforms &&
            metric?.platforms?.length > 0 && (
              <div className="flex items-center gap-1 mr-2">
                {metric?.platforms.length <= 2 ? (
                  // Show all icons if 2 or fewer platforms
                  metric?.platforms?.map((p, index) => (
                    <img
                      key={index}
                      className="h-[14px] w-[14px] flex-shrink-0"
                      src={getPlatformIcon(p)}
                    />
                  ))
                ) : (
                  // Show first icon + count for remaining
                  <>
                    <img
                      className="h-[14px] w-[14px] flex-shrink-0"
                      src={getPlatformIcon(metric?.platforms[0])}
                    />
                    <span className="translate-y-[1px] text-[#02185999]">
                      +{metric?.platforms.length - 1}
                    </span>
                  </>
                )}
              </div>
            )}
          {/* if external metric  */}
          {metric?.sub_type === "EXTERNAL" && (
            <IconDatabase size={14} className={`text-[#02185999] shrink-0 mr-1`} />
          )}
          {/* label */}
          <span className="overflow-hidden text-brand-primary-new text-ellipsis text-[11px] font-normal tracking-[0.24px] relative top-[1px] mr-1">
            {metric.label}
            {metric.show_platforms && <span className="text-[#FF4500]">*</span>}
          </span>
          <RiCloseLine
            onClick={() => onRemove(metric)}
            size={14}
            className={`cursor-pointer h-auto text-brand-primary-400 hover:bg-[#fff] shadow-none hover:!text-[#D92D20] transition-colors duration-150`}
          />
        </SqButton>
      </SqTooltipTrigger>
      {metric?.show_platforms && metric?.platforms && metric?.platforms.length > 0 && (
        <SqTooltipContent portal={true} side="bottom" align="start" alignOffset={20}>
          <p>
            {metric.label}
            <br />
            Available for{" "}
            {metric.platforms
              .map(
                (platform) =>
                  platform.charAt(0).toUpperCase() + platform.slice(1).toLowerCase(),
              )
              .join(", ")}
          </p>
        </SqTooltipContent>
      )}
    </SqTooltip>
  );
};

const SelectedMetrics = ({ selectedMetrics, onMetricChange }: Props) => {
  const [localMetrics, setLocalMetrics] = useState<Metric[]>(selectedMetrics);
  const [isDragging, setIsDragging] = useState(false);
  const [draggingMetricKey, setDraggingMetricKey] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Sync local state with prop changes (when metrics are added/removed externally)
  useEffect(() => {
    if (!isDragging) {
      setLocalMetrics(selectedMetrics);
    }
  }, [selectedMetrics, isDragging]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Handle reorder during drag (update local state only)
  const handleReorder = (newOrder: Metric[]) => {
    setLocalMetrics(newOrder);
  };

  // Handle drag end (commit changes to parent)
  const handleDragEnd = () => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Delay state update to allow drop animation to complete smoothly
    timeoutRef.current = setTimeout(() => {
      setIsDragging(false);
      setDraggingMetricKey(null);
      // Only call onMetricChange if order actually changed
      const hasChanged = localMetrics.some(
        (metric, index) => metric.key !== selectedMetrics[index]?.key,
      );
      if (hasChanged) {
        onMetricChange(localMetrics);
      }
    }, DROP_ANIMATION_DELAY);
  };

  return (
    <SqTooltipProvider>
      {localMetrics.length > 0 && (
        <Reorder.Group
          axis="x"
          values={localMetrics}
          onReorder={handleReorder}
          className="flex items-center gap-1"
          as="div"
        >
          {localMetrics?.map((m) => (
            <Reorder.Item
              key={m.key}
              value={m}
              as="div"
              className="relative"
              onDragStart={() => {
                setIsDragging(true);
                setDraggingMetricKey(m.key);
              }}
              onDragEnd={handleDragEnd}
              layout
              whileDrag={{
                scale: 1.1,
                zIndex: 1000,
              }}
              transition={{
                layout: {
                  type: "spring",
                  stiffness: 400,
                  damping: 30,
                },
              }}
            >
              <SelectedMetricItem
                metric={m}
                onRemove={(metric: Metric) => {
                  const updatedMetrics = selectedMetrics?.filter(
                    (m) => m.key !== metric.key,
                  );
                  onMetricChange(updatedMetrics);
                }}
                isBeingDragged={draggingMetricKey === m.key}
              />
            </Reorder.Item>
          ))}
        </Reorder.Group>
      )}
    </SqTooltipProvider>
  );
};

export default SelectedMetrics;
